import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { OtpInput } from "@/components/forms/otpInput"
import { Button } from "@/components/ui/button"
import { useResendVerification, useVerifyEmail } from "../../hooks/useAuth"
import { verifyEmailByLink } from "../../services/auth.service"
import { useAuthStore } from "../../store/authStore"

export function MobileVerifyEmailScreen() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const verifyEmailMutation = useVerifyEmail()
  const resendVerificationMutation = useResendVerification()

  const magicEmail = searchParams.get("email")
  const magicToken = searchParams.get("token")

  const stateEmail = (location.state as { email?: string })?.email
  const authStoreEmail = useAuthStore(state => state.user?.email)
  const email = magicEmail || stateEmail || authStoreEmail

  const [otp, setOtp] = useState("")
  const [resendCountdown, setResendCountdown] = useState(60)
  const [isVerifyingLink, setIsVerifyingLink] = useState(false)
  const verifyInProgress = useRef(false)

  const isOtpComplete = otp.length === 6
  const isLoading = verifyEmailMutation.isPending || isVerifyingLink

  const verifyMagicLink = useCallback(async () => {
    if (!magicEmail || !magicToken || verifyInProgress.current) {
      return
    }

    verifyInProgress.current = true
    setIsVerifyingLink(true)

    try {
      const result = await verifyEmailByLink(magicEmail, magicToken)
      useAuthStore.getState().setUser(result.user)
      window.location.href = "/onboarding/permissions"
    } catch {
      toast.error("Invalid or expired verification code")
      window.location.href = "/verify-email"
    } finally {
      setIsVerifyingLink(false)
      verifyInProgress.current = false
    }
  }, [magicEmail, magicToken])

  useEffect(() => {
    verifyMagicLink()
  }, [verifyMagicLink])

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  const handleVerify = async () => {
    if (!isOtpComplete || !email) return

    const deviceId = localStorage.getItem("deviceId") || crypto.randomUUID()
    localStorage.setItem("deviceId", deviceId)

    const deviceType = /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop"
    const userAgent = navigator.userAgent

    try {
      await verifyEmailMutation.mutateAsync({
        email,
        code: otp,
        deviceId,
        deviceType,
        userAgent,
      })

      navigate("/onboarding/permissions", { replace: true })
    } catch (error) {
      console.error("Verification error:", error)
    }
  }

  const handleResendCode = async () => {
    if (resendCountdown > 0) return
    if (!email) return

    try {
      await resendVerificationMutation.mutateAsync({ email })
      setResendCountdown(60)
      setOtp("")
    } catch (error) {
      console.error("Resend error:", error)
    }
  }

  return (
    <div className="space-y-0 flex flex-col justify-between h-[calc(100dvh-70px)]">
      <div>
        <div className="text-center mt-5">
          <h2 className="text-xl font-semibold leading-normal text-[#F2F0EB]">
            {t("verifyEmail.title")}
          </h2>
        </div>

        <div className="text-center mt-5">
          <p className="text-sm leading-5.5 text-primary">
            {t("verifyEmail.sentMessage")}{" "}
            <span className="font-semibold">{email || t("verifyEmail.yourEmail")}</span>. <br />
            {t("verifyEmail.instructions")}
          </p>
        </div>

        <div className="flex justify-center mt-8.75">
          <OtpInput
            length={6}
            value={otp}
            onChange={setOtp}
            onComplete={handleVerify}
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="text-center mt-7.5">
          <div className="text-sm leading-5.5 text-primary flex flex-col justify-center">
            {t("verifyEmail.resendHint")}
            <span>
              {t("verifyEmail.resendHint2")}{" "}
              {resendCountdown > 0 ? (
                <span className="font-semibold p-0 h-auto text-primary">
                  {t("verifyEmail.resendCountdown", { resendCountdown })}
                </span>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  onClick={handleResendCode}
                  className="font-semibold p-0 h-auto text-primary"
                  disabled={isLoading}
                >
                  resend
                </Button>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 pb-6 pt-4">
        <Button
          type="button"
          variant="primary-white"
          size="md"
          className="rounded-full uppercase h-12.5 py-3.75 text-sm font-semibold"
          isLoading={isLoading}
          disabled={!isOtpComplete}
          fullWidth
          onClick={handleVerify}
        >
          {t("verifyEmail.continueButton")}
        </Button>
      </div>
    </div>
  )
}
